import { api } from './api';
import { AuthResponse, User } from '../types/api';
import { PagePermissions, NestedPermissions, normalizePermissions, DEFAULT_PAGE_PERMISSIONS } from '../constants/pages';

const USER_KEY = 'warehouse_user';
const TOKEN_KEY = 'warehouse_token';
const PERMISSIONS_KEY = 'warehouse_permissions';

// Re-export User type for convenience
export type { User };

// Extended user with permissions
export interface UserWithPermissions extends User {
  permissions?: NestedPermissions | PagePermissions;
}

// Helper to decode JWT and check expiration
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= exp;
  } catch {
    return true; // If we can't decode, consider it expired
  }
};

export const authService = {
  login: async (credentials: { email: string; password: string }) => {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  },

  // Fetch current user profile with permissions from API
  fetchUserProfile: async (): Promise<UserWithPermissions | null> => {
    try {
      const response = await api.get<UserWithPermissions>('/api/auth/me');
      const userData = response.data;
      
      // Save user data
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      
      // Normalize and save permissions
      if (userData.permissions) {
        const normalizedPermissions = normalizePermissions(userData.permissions);
        localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(normalizedPermissions));
      }
      
      return userData;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  },

  saveUser: (user: User, token: string) => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(TOKEN_KEY, token);
  },

  savePermissions: (permissions: NestedPermissions | PagePermissions) => {
    const normalizedPermissions = normalizePermissions(permissions);
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(normalizedPermissions));
  },

  getUser: (): User | null => {
    const userStr = localStorage.getItem(USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr) as User;
      } catch {
        return null;
      }
    }
    return null;
  },

  getPermissions: (): PagePermissions => {
    const permStr = localStorage.getItem(PERMISSIONS_KEY);
    if (permStr) {
      try {
        return JSON.parse(permStr) as PagePermissions;
      } catch {
        return { ...DEFAULT_PAGE_PERMISSIONS };
      }
    }
    return { ...DEFAULT_PAGE_PERMISSIONS };
  },

  // Check if user has a specific permission
  hasPermission: (permissionKey: keyof PagePermissions): boolean => {
    const permissions = authService.getPermissions();
    return permissions[permissionKey] === true;
  },

  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  // Clears all locally stored session data without navigating.
  clearSession: () => {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
  },

  // Logs the user out and performs a hard reload. Using a full page load (not
  // client-side navigation) guarantees no stale in-memory state survives.
  // This is used for both voluntary logout and forced logout (invalid/expired
  // token, account updated, disabled account, blocked IP, etc.).
  logout: () => {
    authService.clearSession();
    window.location.href = '/login';
  },

  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    
    // Check if token is expired
    if (isTokenExpired(token)) {
      try {
        sessionStorage.setItem('logout_reason', 'Your session expired. Please log in again.');
      } catch {
        // Ignore storage failures.
      }
      authService.logout();
      return false;
    }
    return true;
  },

  isTokenValid: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? !isTokenExpired(token) : false;
  },

  // Check if user is admin (admin has full access)
  isAdmin: (): boolean => {
    const user = authService.getUser();
    return user?.role === 'ADMIN';
  },
};
